// The ceremony controller. Wires session + api + wheel + ui + spinlog into the six states:
//   0 Altar (idle, gated)  1 Seal (commit shown)  2 Settle (silent)  3 Spin (theater)
//   4 Reveal (result)      5 Verify (rotate & reveal → in-browser recompute)
//
// The cardinal rule lives in api.ts (retry the SAME spin, fresh nonce only on
// NONCE_MISMATCH). This file must honor the other half: SETTLE FIRST, THEN animate to the
// returned tier. The wheel never turns before the result is durable.

import { ArcadeApi, AuthError, UpstreamUnavailableError } from './api';
import { audio } from './audio';
import { GAME_ID } from './config';
import {
  DAILY_CAP_LINE,
  HOUSE_LINE,
  INSUFFICIENT_LINE,
  outcomeLine,
  RECONNECT_LINE,
  RITUAL_LINE,
  SEAL_LINE,
} from './copy';
import { defaultClientSeed, SpinLog } from './spinlog';
import { verifySpin } from './fairness';
import type { VerifyResult } from './fairness';
import type { ArcadeUI } from './ui';
import type { Session } from './sdk';
import type { Mode, Settlement, SpinConfig, WeightTable } from './types';
import { REVEAL_BEAT_MS } from './wheel';
import type { Wheel, WheelEntry } from './wheel';

// The settle round-trip is the one window where nobody knows the answer — it gets a
// ceremonial floor so the beat exists even when the API answers in 40ms. Constant across
// all outcomes (pacing, not deception).
const SETTLE_FLOOR_MS = 600;
// After the reveal, the Spin button stays down for a beat — breaks the click-trance and
// gives the result its moment. Harm reduction and juice, same change.
const SPIN_COOLDOWN_MS = 1200;
const RITUAL_KEY = 'arcade:ritual-seen';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export class Arcade {
  private api: ArcadeApi;
  private config: SpinConfig | null = null;
  private balance = 0;
  private wheel: Wheel | null = null;
  private busy = false;
  private pendingRetry: (() => Promise<void>) | null = null;

  constructor(
    private session: Session,
    private ui: ArcadeUI,
  ) {
    this.api = new ArcadeApi(session.accessToken, {
      onReconnecting: () => this.ui.setStatus(RECONNECT_LINE),
      onReconnected: () => this.ui.setStatus(''),
    });
    this.ui.onSpin(() => void this.spin());
    this.ui.onRetry(() => void this.runPendingRetry());
    this.ui.onVerify(() => void this.verify());
    this.ui.onModeChange((mode) => {
      // Backstop only — the tabs are disabled while busy (enterBusy). A mid-spin relayout
      // could land the pointer on a wedge that is not the settled tier.
      if (this.busy) return;
      // Paid and free tables differ — the wheel re-lays out to the mode's own odds.
      this.wheel?.setLayout(this.entriesFor(mode));
      this.applyGate();
    });
    this.ui.onOdds(() => this.ui.setStatus('The odds are published in #arcade via /wheel_odds.'));
    this.ui.onMute(() => {
      const muted = !audio.getMuted();
      audio.setMuted(muted);
      this.ui.setMuteState(muted);
    });
    this.ui.setMuteState(audio.getMuted());
  }

  async load(): Promise<void> {
    try {
      this.config = await this.api.getConfig();
      this.balance = await this.api.getBalance();
    } catch (err) {
      this.handleFatal(err);
      return;
    }
    // Mount the CURRENT MODE's table — wedges are odds-proportional per mode, and every
    // reachable outcome of that mode has a wedge (golden_ticket deliberately excepted: it
    // resolves on the rim, wheel.ts explains). Mode switches call setLayout.
    this.wheel = this.ui.mountWheel(this.entriesFor(this.ui.getMode()));
    this.renderAltar();
  }

  private entriesFor(mode: Mode): WheelEntry[] {
    const table = this.config?.weight_table.tables[mode] ?? [];
    return table.map((e) => ({ tier: e.tier, weightPpm: e.weight }));
  }

  // State 0.
  private renderAltar(): void {
    if (!this.config) return;
    const threshold = this.config.weight_table.params.pity_threshold;
    const pot = this.config.jackpot_pot_fs;
    this.ui.renderTopBar({
      fs: this.balance,
      ticketsText: '·', // ticket inventory count is not exposed by the API (attempt-and-handle)
      paidToday: this.config.paid_spins_today,
      cap: this.config.daily_paid_spin_cap,
      // The shared candle (jackpot pot). '—' until hub-core's pot patch is deployed.
      candleText: pot != null ? `${pot} FS` : '—',
      commitHash: this.config.commit_hash,
    });
    this.ui.renderPity(this.config.pity_counter, threshold);
    this.applyGate();
  }

  private applyGate(): void {
    if (!this.config || this.busy) return;
    const stake = this.config.weight_table.params.stake_fs;
    const mode = this.ui.getMode();
    if (mode === 'paid') {
      if (this.config.paid_spins_today >= this.config.daily_paid_spin_cap) {
        this.ui.setSpinEnabled(false, 'The day is spent');
        this.ui.setStatus(DAILY_CAP_LINE);
        return;
      }
      if (this.balance < stake) {
        this.ui.setSpinEnabled(false, `Need ${stake} FS`);
        this.ui.setStatus(INSUFFICIENT_LINE);
        return;
      }
      this.ui.setSpinEnabled(true, `Offer ${stake} FS`);
    } else {
      // No ticket-count field exists; allow the attempt and handle MISSING_ITEM gracefully.
      this.ui.setSpinEnabled(true, 'Use the Sunday ticket');
    }
    // A terminal message from the previous attempt (MISSING_ITEM, refused spin, transport
    // sadness) survives ONE gate re-render — otherwise the refresh round-trip stomps it
    // with the seal line before the player has read it.
    if (this.stickyStatus) {
      this.ui.setStatus(this.stickyStatus);
      this.stickyStatus = null;
      return;
    }
    // One line of ritual on the very first visit, the fast path forever after.
    this.ui.setStatus(this.ritualSeen() ? SEAL_LINE : RITUAL_LINE);
  }

  private stickyStatus: string | null = null;

  private setStickyStatus(line: string): void {
    this.stickyStatus = line;
    this.ui.setStatus(line);
  }

  private ritualSeen(): boolean {
    try {
      return window.localStorage.getItem(RITUAL_KEY) === '1';
    } catch {
      return true; // storage unavailable — skip the ritual rather than repeat it forever
    }
  }

  private markRitualSeen(): void {
    try {
      window.localStorage.setItem(RITUAL_KEY, '1');
    } catch {
      // best-effort
    }
  }

  // States 1–4.
  private async spin(): Promise<void> {
    if (this.busy || !this.config) return;
    const mode = this.ui.getMode();
    this.enterBusy(mode === 'free' ? 'drawing…' : 'sealing…');
    this.ui.clearResult();
    this.ui.showRetry(false);
    this.ui.setStatus(SEAL_LINE); // State 1 — the seal is already on screen.

    try {
      // State 2 — settle silently, under a rising tone with a ceremonial floor: the
      // round-trip is the one honest window of shared ignorance, so it gets the beat.
      audio.riser();
      const sealedAt = performance.now();
      const settlement =
        mode === 'paid'
          ? await this.api.settle(this.config.epoch, this.config.nonce)
          : await this.api.ticketSpend(crypto.randomUUID(), this.config.epoch, this.config.nonce);
      const floorLeft = SETTLE_FLOOR_MS - (performance.now() - sealedAt);
      if (floorLeft > 0) await sleep(floorLeft);
      await this.applySettlement(settlement, mode);
    } catch (err) {
      this.handleSpinError(err, mode);
    } finally {
      if (!this.pendingRetry) this.exitBusy();
    }
  }

  private async applySettlement(settlement: Settlement, mode: Mode): Promise<void> {
    switch (settlement.status) {
      case 'OK':
        await this.playOutcome(settlement, mode);
        break;
      case 'DAILY_CAP':
        this.ui.setStatus(DAILY_CAP_LINE);
        break;
      case 'INSUFFICIENT_FUNDS':
        this.ui.setStatus(INSUFFICIENT_LINE);
        break;
      case 'MISSING_ITEM':
        this.setStickyStatus('No Sunday ticket to spend yet. It arrives with the week.');
        break;
      default:
        // NONCE_MISMATCH / STALE after exhausted retries, or an INVALID_*: nothing moved.
        this.setStickyStatus('The wheel refused that spin. Nothing left your hand — try again.');
        break;
    }
    await this.refresh();
  }

  // State 3 (spin theater) → State 4 (reveal). The FS already moved in State 2.
  private async playOutcome(settlement: Settlement, mode: Mode): Promise<void> {
    if (settlement.spin_id) {
      this.spinLog().logSpin({
        epoch: settlement.epoch ?? this.config!.epoch,
        nonce: settlement.nonce ?? this.config!.nonce,
        mode,
        prizeTier: settlement.outcome.prize_tier,
        prizeFs: settlement.outcome.prize_fs,
        commitHash: settlement.commit_hash ?? '',
        weightTableHash: settlement.weight_table_hash ?? '',
        pityAtSettle: settlement.pity_at_settle,
        clientSeedUsed: settlement.client_seed_used ?? '',
        spinId: settlement.spin_id,
        settledAt: Date.now(),
      });
    }
    const tier = settlement.outcome.prize_tier;
    const potBefore = this.config?.jackpot_pot_fs;
    const rakeFs = this.config?.weight_table.params.rake_fs ?? 5;
    // Net from the settlement's own ledger leg — the celebration gate below and the card
    // headline must agree, and neither may trust a possibly-stale cached balance.
    const net = mode === 'paid' ? (settlement.deltas['user'] ?? 0) : 0;

    // State 3 — the theater. Uniform boundary ticks (pitch from velocity alone), constant
    // duration for every tier, thunk on landing, then the reveal beat.
    await this.wheel?.spinTo(tier, { onTick: (crossed, velocity) => audio.tick(crossed, velocity) });
    if (tier !== 'golden_ticket') audio.thunk();
    await sleep(REVEAL_BEAT_MS);

    // State 4 — the reveal ladder. The full-screen jackpot moment and its fanfare fire
    // ONLY when the spin is net-positive: a pot smaller than the stake is a loss, and a
    // celebrated loss is the exact LDW the canon bans. The honest card carries it instead.
    if (tier === 'golden_ticket') {
      this.ui.goldenMoment(outcomeLine(tier, settlement.outcome.prize_fs, 0));
    } else if (tier === 'jackpot' && net > 0) {
      this.ui.jackpotMoment(settlement.outcome.jackpot_win, potBefore ?? undefined);
    }
    this.ui.revealOutcome(
      settlement,
      mode,
      potBefore != null ? { potBefore, rakeFs } : undefined,
    );
    audio.sting(tier === 'jackpot' && net <= 0 ? 'fs_5_25' : tier);
    this.ui.tweenBalance(settlement.new_balance);
    this.ui.setStatus(HOUSE_LINE);
    this.markRitualSeen();

    // The cooldown: the result gets its moment before the button re-arms.
    await sleep(SPIN_COOLDOWN_MS);
    this.wheel?.clearGolden();
  }

  private handleSpinError(err: unknown, mode: Mode): void {
    if (err instanceof UpstreamUnavailableError) {
      // The settle is genuinely unknown — but idempotent. Offer a manual retry of the SAME
      // spin; never a fresh one. The player's FS is either untouched or already recorded.
      this.ui.setStatus(RECONNECT_LINE);
      this.ui.showRetry(true);
      this.pendingRetry = async () => {
        this.ui.showRetry(false);
        this.ui.setStatus(RECONNECT_LINE);
        try {
          const settlement = await err.retrySame();
          this.pendingRetry = null;
          await this.applySettlement(settlement, mode);
          this.exitBusy();
        } catch (again) {
          if (again instanceof UpstreamUnavailableError) {
            this.ui.setStatus(RECONNECT_LINE);
            this.ui.showRetry(true);
            return; // keep pendingRetry armed
          }
          this.pendingRetry = null;
          this.handleFatal(again);
        }
      };
      return;
    }
    if (err instanceof AuthError) {
      this.ui.fatal('Your session expired. Close and reopen the Arcade.');
      return;
    }
    this.setStickyStatus('Something went sideways. Nothing was charged — try again.');
  }

  private async runPendingRetry(): Promise<void> {
    if (this.pendingRetry) await this.pendingRetry();
  }

  // State 5 — rotate the seed (revealing the retiring epoch's server_seed + client_seed) and
  // recompute every recorded spin of that epoch in-browser.
  private async verify(): Promise<void> {
    if (this.busy || !this.config) return;
    this.enterBusy();
    this.ui.setVerifyBusy(true, 'revealing…');
    try {
      const newClientSeed = `sanctum:${this.session.uid}:${crypto.randomUUID()}`;
      const rotation = await this.api.rotateSeed(newClientSeed);
      this.spinLog().rememberClientSeed(rotation.active.epoch, newClientSeed);

      const revealed = rotation.revealed;
      const clientSeed =
        revealed.client_seed ||
        this.spinLog().clientSeedForEpoch(revealed.epoch) ||
        (revealed.epoch === 0 ? defaultClientSeed(this.session.uid, GAME_ID) : '');

      const table = this.tableForHash(revealed.weight_table_hash);
      const spins = this.spinLog().spinsForEpoch(revealed.epoch);
      let results: VerifyResult[] = [];
      if (clientSeed && table) {
        results = await Promise.all(
          spins.map((s) =>
            verifySpin(
              {
                nonce: s.nonce,
                mode: s.mode,
                prizeTier: s.prizeTier,
                prizeFs: s.prizeFs,
                commitHash: s.commitHash,
                weightTableHash: s.weightTableHash,
                pityAtSettle: s.pityAtSettle,
              },
              {
                serverSeedHex: revealed.server_seed,
                clientSeed,
                weightTable: table.table,
                weightTableJson: table.json,
              },
            ),
          ),
        );
      }
      this.ui.renderVerify(results, revealed.epoch);
      await this.refresh();
    } catch (err) {
      if (err instanceof AuthError) {
        this.ui.fatal('Your session expired. Close and reopen the Arcade.');
      } else {
        this.ui.setStatus('Could not rotate the seed just now. Your past spins are still on record.');
      }
    } finally {
      this.ui.setVerifyBusy(false, '🔍 Verify — rotate & reveal my seed');
      this.exitBusy();
    }
  }

  // The candidate table (parsed for mapping + the exact canonical bytes for hashing) for a
  // revealed epoch. hub-core's weights are pinned/stable, so the current config's table is
  // the one the retired epoch used; if a future weight change ever breaks that, verifySpin
  // hashes weight_table_json and reports an honest table_hash_mismatch rather than lying.
  private tableForHash(_hash: string): { table: WeightTable; json: string } | null {
    if (!this.config) return null;
    return { table: this.config.weight_table, json: this.config.weight_table_json };
  }

  private async refresh(): Promise<void> {
    try {
      this.config = await this.api.getConfig();
      this.balance = await this.api.getBalance();
      this.renderAltar();
    } catch (err) {
      this.handleFatal(err);
    }
  }

  private enterBusy(spinLabel?: string): void {
    this.busy = true;
    this.ui.setSpinEnabled(false, spinLabel);
    this.ui.setModeEnabled(false);
  }

  private exitBusy(): void {
    this.busy = false;
    this.ui.setModeEnabled(true);
    this.applyGate();
  }

  private handleFatal(err: unknown): void {
    if (err instanceof AuthError) {
      this.ui.fatal('Your session expired. Close and reopen the Arcade.');
      return;
    }
    this.ui.fatal('The Arcade could not reach the Sanctum. Try again shortly.');
  }

  private _log: SpinLog | null = null;
  private spinLog(): SpinLog {
    if (!this._log) this._log = new SpinLog(this.session.uid);
    return this._log;
  }
}
